import { ROAD_CLASSES, type County } from '@hotlist/shared';

/**
 * The front door's living background: the county itself.
 *
 * Every landing page reaches for a hero image, and this game owns something no
 * image bank has -- the actual road network of Waukesha County, already sitting
 * in the client. So the backdrop is the real 53,481 roads, drawn once to an
 * offscreen layer, with traffic drifting along real edges and plate reads
 * flashing at real junctions while a wire ticker narrates them. The page shows
 * the game's whole premise before a word of copy is read: a county, moving,
 * being recorded.
 *
 * Nothing here leaks. The flash points are random junctions, never the actual
 * camera network -- civilians earn that map in the game, and the front door is
 * not going to hand it out.
 */

/** How many drifting cars the backdrop runs. Enough to read as traffic. */
const DRIFTERS = 130;

/** How often the county is asked what just happened, in milliseconds. */
const WIRE_EVERY_MS = 4000;

/** One entry off /pulse's wire: a real read, coarsened to its town. */
interface WireRead {
  seq: number;
  plate: string;
  town: string;
  hit: boolean;
}

interface Drifter {
  edge: number;
  /** 0..1 along the edge. */
  t: number;
  /** Fraction per second, sized so class speed reads as class speed. */
  rate: number;
  forward: boolean;
}

interface Flash {
  x: number;
  y: number;
  age: number;
}

export class LandingScene {
  private readonly county: County;
  private readonly canvas: HTMLCanvasElement;
  private readonly wire: HTMLElement | null;
  private roadsLayer: HTMLCanvasElement | null = null;
  private drifters: Drifter[] = [];
  private flashes: Flash[] = [];
  /**
   * The last read shown, so a poll only plays what is new.
   *
   * The wire is real: the server streams its own recent detections --
   * simulation traffic only, coarsened to the town -- and this page plays
   * them as they happen. The first version invented plates client-side and
   * was caught within a day showing a read in Eagle, which holds no
   * documented camera. A front door whose banner is "the readers are at
   * their real locations" narrates real reads or none.
   */
  private lastSeq = 0;
  private wireTimer = 0;
  /** Edges worth putting traffic on: the ones drawn bright enough to see. */
  private visibleEdges: number[] = [];
  private frame = 0;
  private running = false;
  private last = 0;
  /** World-to-screen: scale and offsets, recomputed on resize. */
  private view = { scale: 1, ox: 0, oy: 0 };
  private readonly onResize = (): void => this.fit();

  constructor(county: County, canvas: HTMLCanvasElement, wire: HTMLElement | null) {
    this.county = county;
    this.canvas = canvas;
    this.wire = wire;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.fit();
    this.seed();
    window.addEventListener('resize', this.onResize);
    // The county's own wire, played as it happens.
    this.poll();
    this.wireTimer = window.setInterval(() => this.poll(), WIRE_EVERY_MS);
    this.last = performance.now();
    const tick = (now: number): void => {
      if (!this.running) return;
      const dt = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      this.step(dt);
      this.draw();
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  /** The door closed. The game owns the GPU now; this stops entirely. */
  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    window.clearInterval(this.wireTimer);
  }

  /** Ask the county what just happened, and play anything new. */
  private poll(): void {
    void fetch('/pulse')
      .then((r) => (r.ok ? r.json() : null))
      .then((pulse: { wire?: WireRead[] } | null) => {
        if (!pulse || !Array.isArray(pulse.wire)) return;
        // Oldest first, so the feed reads in the order it happened.
        const fresh = pulse.wire.filter((read) => read.seq > this.lastSeq).reverse();
        for (const read of fresh) {
          this.lastSeq = Math.max(this.lastSeq, read.seq);
          this.play(read);
        }
      })
      .catch(() => {});
  }

  /** One real read: a bloom at its town, a line on the wire. */
  private play(read: WireRead): void {
    const place = this.county.places.find((p) => p.name === read.town);
    if (place) {
      // Town center with jitter, never the pole. Which municipalities have
      // readers is public; where exactly is earned in the game.
      this.flashes.push({
        x: place.x + (Math.random() - 0.5) * 1400,
        y: place.y + (Math.random() - 0.5) * 1400,
        age: 0,
      });
    }
    if (!this.wire) return;
    const row = document.createElement('div');
    row.className = read.hit ? 'wire-row hit' : 'wire-row';
    row.textContent = read.hit
      ? `ALERT · ${read.plate} · ${read.town} · unit dispatched`
      : `READ · ${read.plate} · ${read.town}`;
    this.wire.prepend(row);
    while (this.wire.childElementCount > 7) this.wire.lastElementChild?.remove();
  }

  private fit(): void {
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);

    const { minX, minY, maxX, maxY } = this.county.bounds;
    // Cover, not contain: the county overfills the viewport so there are no
    // empty corners, and the middle of the county is the middle of the page.
    const scale = Math.max(
      this.canvas.width / (maxX - minX),
      this.canvas.height / (maxY - minY),
    ) * 1.08;
    this.view = {
      scale,
      ox: this.canvas.width / 2 - ((minX + maxX) / 2) * scale,
      // Screen y grows downward; world y grows northward.
      oy: this.canvas.height / 2 + ((minY + maxY) / 2) * scale,
    };
    this.paintRoads();
  }

  private sx(x: number): number {
    return x * this.view.scale + this.view.ox;
  }

  private sy(y: number): number {
    return -y * this.view.scale + this.view.oy;
  }

  /**
   * The road network, drawn once per resize to its own layer.
   *
   * Fifty-three thousand line segments are nothing to draw once and far too
   * many to draw sixty times a second; the animation blits this and spends
   * its frame budget on the hundred things that move.
   */
  private paintRoads(): void {
    const county = this.county;
    const layer = document.createElement('canvas');
    layer.width = this.canvas.width;
    layer.height = this.canvas.height;
    const g = layer.getContext('2d');
    if (!g) return;

    const major = new Set(
      ['motorway', 'trunk', 'primary'].map((name) => ROAD_CLASSES.indexOf(name as never)),
    );
    const mid = new Set(
      ['secondary', 'tertiary', 'motorway_link'].map((name) =>
        ROAD_CLASSES.indexOf(name as never),
      ),
    );

    // Three passes, faint to bright, so the arterials sit on top.
    const passes: [Set<number> | null, string, number][] = [
      [null, 'rgba(96, 112, 138, 0.16)', 0.6],
      [mid, 'rgba(140, 158, 186, 0.34)', 1.0],
      [major, 'rgba(232, 163, 61, 0.5)', 1.6],
    ];
    for (const [only, style, width] of passes) {
      g.strokeStyle = style;
      g.lineWidth = width;
      g.beginPath();
      for (let e = 0; e < county.edgeCount; e++) {
        const cls = county.edgeClass[e] as number;
        if (only ? !only.has(cls) : major.has(cls) || mid.has(cls)) continue;
        const a = county.edgeA[e] as number;
        const b = county.edgeB[e] as number;
        g.moveTo(this.sx(county.nodeX[a] as number), this.sy(county.nodeY[a] as number));
        g.lineTo(this.sx(county.nodeX[b] as number), this.sy(county.nodeY[b] as number));
      }
      g.stroke();
    }
    this.roadsLayer = layer;
  }

  private seed(): void {
    /**
     * Traffic goes only where roads are visible.
     *
     * Picking edges uniformly put most of the dots on residential streets
     * drawn at 16% opacity -- a dot on an invisible road reads as a dot on
     * nothing, and the report said exactly that: appearing wherever, not on
     * roads. The arterials are the drawn ones, so they are the driven ones.
     */
    const county = this.county;
    const drawn = new Set(
      ['motorway', 'trunk', 'primary', 'secondary', 'tertiary'].map((name) =>
        ROAD_CLASSES.indexOf(name as never),
      ),
    );
    this.visibleEdges = [];
    for (let e = 0; e < county.edgeCount; e++) {
      if (drawn.has(county.edgeClass[e] as number)) this.visibleEdges.push(e);
    }
    this.drifters = [];
    for (let i = 0; i < DRIFTERS; i++) this.drifters.push(this.spawn());
  }

  private spawn(): Drifter {
    const county = this.county;
    const pool = this.visibleEdges;
    const edge =
      pool.length > 0
        ? (pool[Math.floor(Math.random() * pool.length)] as number)
        : Math.floor(Math.random() * county.edgeCount);
    const speed = county.edgeSpeed[edge] as number;
    const length = Math.max(20, county.edgeLen[edge] as number);
    return {
      edge,
      t: Math.random(),
      // Real speed over real length, sped up a touch so the map reads alive
      // at county zoom rather than geologic.
      rate: (speed * 2.2) / length,
      forward: Math.random() < 0.5,
    };
  }

  private step(dt: number): void {
    for (let i = 0; i < this.drifters.length; i++) {
      const d = this.drifters[i] as Drifter;
      d.t += (d.forward ? 1 : -1) * d.rate * dt;
      if (d.t < 0 || d.t > 1) this.drifters[i] = this.spawn();
    }

    for (const flash of this.flashes) flash.age += dt;
    this.flashes = this.flashes.filter((flash) => flash.age < 2.4);

  }

  private draw(): void {
    const g = this.canvas.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.roadsLayer) g.drawImage(this.roadsLayer, 0, 0);

    // Traffic: points sliding along the drawn roads, fading in at one end of
    // their edge and out at the other so nothing ever pops into existence.
    const county = this.county;
    for (const d of this.drifters) {
      const a = d.edge < county.edgeCount ? (county.edgeA[d.edge] as number) : 0;
      const b = d.edge < county.edgeCount ? (county.edgeB[d.edge] as number) : 0;
      const x = (county.nodeX[a] as number) * (1 - d.t) + (county.nodeX[b] as number) * d.t;
      const y = (county.nodeY[a] as number) * (1 - d.t) + (county.nodeY[b] as number) * d.t;
      const alpha = 0.85 * Math.min(1, d.t * 6, (1 - d.t) * 6);
      g.fillStyle = `rgba(226, 234, 246, ${alpha.toFixed(3)})`;
      g.fillRect(this.sx(x) - 1, this.sy(y) - 1, 2.4, 2.4);
    }

    // Reads: an amber ring blooming and fading where a plate just went past.
    for (const flash of this.flashes) {
      const r = 4 + flash.age * 26;
      const fade = Math.max(0, 1 - flash.age / 2.4);
      g.strokeStyle = `rgba(232, 163, 61, ${(0.55 * fade).toFixed(3)})`;
      g.lineWidth = 1.6;
      g.beginPath();
      g.arc(this.sx(flash.x), this.sy(flash.y), r, 0, Math.PI * 2);
      g.stroke();
    }
  }
}
